import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { ICliente } from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';

@Component({
  selector: 'app-detalles-cliente',
  templateUrl: './detalles-cliente.component.html',
  styleUrl: '../../../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesClienteComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);

  // El mismo permiso que pide el listado para editar
  public puedeEditar = this.helper.tienePermiso(
    'Administración - Editar clientes',
  );

  public selectedTab = 0;
  public idCliente = '';
  public cliente?: ICliente;
  public loading: boolean = true;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  /** Estado de cuenta con los colores del marco. */
  get tonoEstadoCuenta(): string {
    switch (this.cliente?.estadoDeCuenta) {
      case 'Activo':
        return 'ok';
      case 'Moroso':
        return 'advertencia';
      case 'Suspendido':
        return 'alerta';
      default:
        return 'neutro';
    }
  }

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'eventos', 'notas', 'contactos'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listarCliente() {
    // Si el cliente no existe (se borró, no hay acceso) el getById falla: sin
    // el catch ngOnInit se cortaba y la página quedaba con el spinner.
    try {
      this.cliente = await firstValueFrom(this.lista2.cliente(this.idCliente));
    } catch {
      this.cliente = undefined;
    }
  }

  public editar(): void {
    this.router.navigate([
      'main/administracion/clientes/editar',
      this.idCliente,
    ]);
  }

  /**
   * Carga el cliente del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de un cliente a otro (desde la
   * jerarquía, por ejemplo).
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.cliente = undefined;
    this.idCliente = id ?? '';
    await this.listarCliente();
    this.loading = false;
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
